import React, { useState, useEffect, useRef } from 'react';
import { CATEGORIES } from '../data';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Terminal, Cpu, Database, Play, CheckCircle, RefreshCw, 
  Layers, Shield, Activity, Coins, Globe, Key, FileText, 
  ChevronRight, Search, CheckSquare, Settings, Sliders, Flame, 
  AlertTriangle, HelpCircle, ShieldCheck
} from 'lucide-react';

export interface SubNodeItem {
  id: number;
  name: string;
  categoryName: string;
  categoryId: number;
  activity: number;
  temp: number;
  status: 'ONLINE' | 'STANDBY' | 'LOAD_BURST' | 'THROTTLED';
  txVolume: number;
}

export function NexoraX111SubNodesDirectory() {
  // Extract first 112 subnode details from real categories
  const all112SubNodesMeta = React.useMemo(() => {
    const list: Array<{ name: string; categoryName: string; categoryId: number }> = [];
    CATEGORIES.forEach(cat => {
      cat.nodes.forEach(node => {
        list.push({
          name: node,
          categoryName: cat.title.replace(/^CATEGORY \d+:\s*/i, '').trim(),
          categoryId: cat.id
        });
      });
    });
    
    // Safety buffer
    while (list.length < 112) {
      list.push({
        name: `Neural Synapse Protocol #${list.length + 1}`,
        categoryName: "UNALLOCATED GRID STORAGE",
        categoryId: 99
      });
    }
    
    return list.slice(0, 112);
  }, []);

  // Set initial status and live telemetry metrics states
  const [nodes, setNodes] = useState<SubNodeItem[]>(() => {
    return all112SubNodesMeta.map((meta, idx) => {
      const initialActivity = Math.floor(Math.random() * 45) + 20; // 20 to 65%
      const initialTemp = Math.floor(initialActivity * 0.45) + 35;
      const initialTx = Math.floor(initialActivity * 195) + 400;
      
      let status: SubNodeItem['status'] = 'ONLINE';
      if (initialActivity < 25) status = 'STANDBY';
      else if (initialActivity > 75) status = 'LOAD_BURST';

      return {
        id: idx + 1,
        name: meta.name,
        categoryName: meta.categoryName,
        categoryId: meta.categoryId,
        activity: initialActivity,
        temp: initialTemp,
        status,
        txVolume: initialTx,
      };
    });
  });

  const [selectedNodeId, setSelectedNodeId] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ONLINE' | 'STANDBY' | 'LOAD_BURST' | 'THROTTLED'>('ALL');
  const [isPlaying, setIsPlaying] = useState(true);
  const [boostMode, setBoostMode] = useState(false);

  // Focus simulation sliders
  const [customLoad, setCustomLoad] = useState<number | null>(null);
  const [customTxVolume, setCustomTxVolume] = useState<number | null>(null);

  // Interactive diagnostic parameters
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [terminalLogs, setTerminalLogs] = useState<string[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Fluctuating real-time updates for telemetry lists
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setNodes(prev => {
        return prev.map(node => {
          const shiftMax = boostMode ? 15 : 6;
          const shift = Math.floor(Math.random() * (shiftMax * 2 + 1)) - shiftMax;
          
          let nextActivity = node.activity + shift;
          if (boostMode && nextActivity < 45) {
            nextActivity += 18;
          }
          nextActivity = Math.max(5, Math.min(100, nextActivity));
          
          const nextTemp = Math.floor(nextActivity * 0.44) + 36;
          const nextTxVolume = node.txVolume + Math.floor(Math.random() * 8) + (node.activity > 70 ? 450 : 2);
          
          let nextStatus: SubNodeItem['status'] = 'ONLINE';
          if (nextActivity < 20) {
            nextStatus = 'STANDBY';
          } else if (nextActivity > 80) {
            nextStatus = 'LOAD_BURST';
          } else if (nextActivity > 92 && nextTemp > 76) {
            nextStatus = 'THROTTLED';
          }

          return {
            ...node,
            activity: nextActivity,
            temp: nextTemp,
            txVolume: nextTxVolume,
            status: nextStatus
          };
        });
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [isPlaying, boostMode]);

  // Sync scroll in console log output
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [terminalLogs]);

  // Aggregate values
  const activeCount = nodes.filter(n => n.status !== 'STANDBY').length;
  const systemTempAverage = parseFloat((nodes.reduce((sum, curr) => sum + curr.temp, 0) / 112).toFixed(1));
  const aggregateTxVolume = nodes.reduce((sum, curr) => sum + curr.txVolume, 0);

  // Currently focused Node item
  const selectedNode = nodes.find(n => n.id === selectedNodeId) || nodes[0];

  // Manual values overridden or live parameters
  const currentLoadValue = customLoad !== null ? customLoad : selectedNode.activity;
  const currentTxValue = customTxVolume !== null ? customTxVolume : selectedNode.txVolume;

  // Single Multi-stage tax calculator matching absolute accounting protocol
  const calculateTaxValues = (grossValue: number) => {
    const levy1Percent = grossValue * 0.01;
    const charitySplit = levy1Percent / 3;
    const lifetimeTax20Percent = (grossValue - levy1Percent) * 0.20;
    const netSettled = grossValue - levy1Percent - lifetimeTax20Percent;

    return {
      levy: levy1Percent,
      charity: charitySplit,
      gavelFee: lifetimeTax20Percent,
      netSettlement: netSettled
    };
  };

  const selectedNodeTax = calculateTaxValues(currentTxValue);
  const overallGlobalTax = calculateTaxValues(aggregateTxVolume);

  // Search/Filter matching sequence
  const filteredNodesList = nodes.filter(node => {
    const matchesSearch = 
      node.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      node.categoryName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `node-${node.id}`.includes(searchQuery.toLowerCase()) ||
      node.id.toString() === searchQuery;
    
    if (!matchesSearch) return false;
    if (statusFilter !== 'ALL' && node.status !== statusFilter) return false;
    return true;
  });

  // Diagnostic compiler trigger logs
  const getCustomLogs = (subNode: SubNodeItem): string[] => {
    const logs = [
      "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.",
      `[MANDATE] Chief Architect Command triggered sequentially on Subnode #${subNode.id.toString().padStart(3, '0')}`,
      `[NODE_CORE] Active focus target: [${subNode.name}]`,
      `[REST_SLA] Verifying 0.8s Ultra-Low Latency constraints across sector SEC-${subNode.categoryId.toString().padStart(2, '0')}`,
      `[ATW_VAL] Direct-billing access verified under Secure Payment Provider scheme Secure Credential Stored Server-Side | Corporate: @nexorax.co.uk`,
      "[SLA_TEST] Running network stress compile. Sending 2,048 telemetry handshakes...",
      `[STRESS_RESULT] Subnode Load: ${currentLoadValue}% | Thermal State: ${subNode.temp}°C [SAFEBOUNDS]`,
      "--- TIERS SPLIT SETTLEMENT LEDGER ---",
      `[ACCOUNT_CORE] Cumulative Telemetry Inbound Volume: £${currentTxValue.toLocaleString()}`,
      `[PERPETUAL_LEVY] 1% 3% Community Allocation calculated: £${selectedNodeTax.levy.toLocaleString()}`,
      `[CHARITY_1] Split to Dunfermline Area Restoration Trust (33.33%): £${selectedNodeTax.charity.toLocaleString()}`,
      `[CHARITY_2] Split to Global Digital NexoraXty Fund (33.33%): £${selectedNodeTax.charity.toLocaleString()}`,
      `[CHARITY_3] Split to UK Relief Alliance (33.33%): £${selectedNodeTax.charity.toLocaleString()}`,
      `[GAVEL_FEE] 20% Tax (Gavel Free Settlement extracted): £${selectedNodeTax.gavelFee.toLocaleString()}`,
      `[REDUNDANCY] Replicating snapshot dataset directly: /var/www/nexorax_source ↔ gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain (CloudSync-02 verified)`,
      `[SecurePayment_ROUTE] Net Liquidity Settlement of £${selectedNodeTax.netSettlement.toLocaleString()} routed into Secure Payment Provider: Sort Verified Payment Configuration | Acc Secure Billing Provider`,
      `[COMPLETED] Execution sequence compiled flawlessly inside ${latencyStandardTimeDescription()}s.`
    ];
    return logs;
  };

  const latencyStandardTimeDescription = () => {
    return (0.4 + (currentLoadValue / 1800)).toFixed(3);
  };

  const handleExecuteSequence = () => {
    if (isRunning) return;
    setIsRunning(true);
    setProgress(0);
    setTerminalLogs([]);

    const logArray = getCustomLogs(selectedNode);
    let index = 0;
    
    const interval = setInterval(() => {
      if (index < logArray.length) {
        setTerminalLogs(prev => [...prev, logArray[index]]);
        setProgress(Math.round(((index + 1) / logArray.length) * 100));
        index++;
      } else {
        clearInterval(interval);
        setIsRunning(false);
      }
    }, 110);
  };

  // Reset slider custom overrides when selected node ID changes
  useEffect(() => {
    setCustomLoad(null);
    setCustomTxVolume(null);
    setTerminalLogs([]);
  }, [selectedNodeId]);

  return (
    <div id="NexoraX-111nodes-directory-container" className="flex flex-col gap-5 text-left">
      
      {/* Preamble Verification Box */}
      <div className="border border-indigo-500/10 bg-indigo-950/10 px-4 py-3 rounded-xl flex flex-col gap-1 select-all">
        <span className="font-mono text-[8px] text-indigo-400 uppercase tracking-widest leading-none font-extrabold">
          Neural Core Authorization Layer // NexoraX Preamble Verification
        </span>
        <div className="font-mono text-[10px] text-indigo-300 font-extrabold leading-relaxed uppercase">
          In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
        </div>
      </div>

      <div className="glass-panel border border-cyan-500/15 rounded-2xl p-5 bg-gradient-to-r from-black/85 via-black/45 to-[#020914]/20 shadow-2xl relative overflow-hidden flex flex-col gap-4">
        
        {/* Module Header Panel */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-white/5 pb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Layers className="w-8 h-8 text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-pulse" />
              <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
              </span>
            </div>
            <div>
              <h2 className="text-lg font-black font-sans text-slate-100 uppercase tracking-normal flex items-center gap-2">
                112 Sub-Nodes Live Navigation Directory
              </h2>
              <span className="font-mono text-[9px] text-emerald-400 uppercase font-black tracking-widest">
                High-Density Performance Registry &amp; Operational Cockpit
              </span>
            </div>
          </div>

          {/* Quick controls */}
          <div className="flex flex-wrap items-center gap-2 font-mono text-[9px]">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className={`px-3 py-1.5 rounded-lg border font-black uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1.5 ${
                isPlaying 
                  ? 'border-emerald-500/30 text-emerald-400 bg-emerald-950/20' 
                  : 'border-amber-500/40 text-amber-400 bg-amber-950/20'
              }`}
            >
              <RefreshCw size={10} className={isPlaying ? 'animate-spin text-emerald-400' : ''} />
              <span>{isPlaying ? 'TELEMETRY LIVE' : 'PAUSED'}</span>
            </button>

            <button
              onClick={() => setBoostMode(!boostMode)}
              className={`px-3 py-1.5 rounded-lg border font-black uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1.5 ${
                boostMode 
                  ? 'border-rose-500 bg-rose-950/30 text-rose-300' 
                  : 'border-white/10 text-slate-400 hover:border-emerald-500/40'
              }`}
              title="Enhance sub-node load simulation values"
            >
              <Flame size={10} className={boostMode ? 'text-rose-450 animate-bounce' : 'text-slate-500'} />
              <span>BOOST MULTIPLIER: {boostMode ? 'ACTIVE' : 'REGULAR'}</span>
            </button>
          </div>
        </div>

        {/* Dynamic global metrics panel */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-black/50 border border-white/5 p-4 rounded-xl">
          <div className="flex flex-col gap-1">
            <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest">System Density Capacity</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-black text-white font-mono">112</span>
              <span className="text-[9px] text-[#e840f3] font-mono uppercase font-black">SUB-NODES</span>
            </div>
            <div className="w-full bg-slate-950 h-1 rounded-full overflow-hidden">
              <div className="bg-[#e840f3] h-full" style={{ width: '100%' }} />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest">Nodes Active / Simulating</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-black text-emerald-400 font-mono">{activeCount}</span>
              <span className="text-[9px] text-slate-400 font-mono">/ 112 ONLINE</span>
            </div>
            <div className="w-full bg-slate-950 h-1 rounded-full overflow-hidden">
              <div className="bg-emerald-400 h-full Transition-all duration-500" style={{ width: `${(activeCount / 112) * 100}%` }} />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest">Avg Thermal Core</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-black text-amber-500 font-mono">{systemTempAverage}°C</span>
              <span className="text-[9px] text-slate-400 font-mono uppercase">LIMIT 85°C</span>
            </div>
            <div className="w-full bg-slate-950 h-1 rounded-full overflow-hidden">
              <div className="bg-amber-500 h-full transition-all duration-500" style={{ width: `${(systemTempAverage / 100) * 100}%` }} />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest">Aggregated Vol (112 Nodes)</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm font-black text-rose-400 font-mono">£{aggregateTxVolume.toLocaleString()}</span>
              <span className="text-[8px] text-[#e840f3] font-mono uppercase font-bold">In-FLUX</span>
            </div>
            <div className="text-[7.5px] font-mono text-slate-500 uppercase font-black leading-none mt-1">
              3% Community Allocation Extraction Sync Enabled
            </div>
          </div>
        </div>

        {/* Dual Panel Main Interface Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          
          {/* LEFT 112 SUB-NODES SCROLLABLE DIRECTORY LIST (takes 5 out of 12 columns) */}
          <div className="lg:col-span-5 bg-black/40 border border-white/5 p-4 rounded-xl flex flex-col h-[670px]">
            
            {/* Embedded node directory search & filter tools */}
            <div className="flex flex-col gap-2.5 pb-3 border-b border-white/5 mb-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[9px] uppercase font-black text-slate-400 tracking-wide flex items-center gap-1.5">
                  <Cpu size={12} className="text-emerald-400" />
                  Telemetry Registry Indice (1-112 Node List)
                </span>
                <span className="text-[8px] font-mono text-emerald-400 bg-emerald-950/20 px-2 py-0.5 rounded border border-emerald-500/25">
                  {filteredNodesList.length} Nodes MATCH
                </span>
              </div>

              {/* Dynamic search bar */}
              <div className="relative w-full bg-slate-950 border border-white/10 hover:border-emerald-500/20 focus-within:border-emerald-500/40 rounded-lg leading-none flex items-center transition-all duration-200">
                <div className="pl-2.5 pr-1.5">
                  <Search className="w-3.5 h-3.5 text-emerald-500/70" />
                </div>
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search 112 registry by name or section name..."
                  className="w-full bg-transparent text-slate-100 font-mono placeholder-slate-700 focus:outline-none text-[10px] py-1.5"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="pr-2 text-[9px] font-mono text-slate-500 hover:text-white"
                  >
                    x
                  </button>
                )}
              </div>

              {/* Filtering Chips grouped row */}
              <div className="flex flex-wrap gap-1">
                {(['ALL', 'ONLINE', 'STANDBY', 'LOAD_BURST', 'THROTTLED'] as const).map(tier => (
                  <button
                    key={tier}
                    onClick={() => setStatusFilter(tier)}
                    className={`px-1.5 py-0.5 border rounded text-[7.5px] font-mono font-black uppercase transition-all cursor-pointer ${
                      statusFilter === tier
                        ? 'border-emerald-500 bg-emerald-950/20 text-emerald-300'
                        : 'border-white/5 bg-black/40 text-slate-500 hover:border-white/10 hover:text-slate-300'
                    }`}
                  >
                    {tier}
                  </button>
                ))}
              </div>
            </div>

            {/* Sub-node rendering scroll block with performance virtualization (React simple layout filter check) */}
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1.5 pr-1.5 pb-2">
              {filteredNodesList.length > 0 ? (
                filteredNodesList.map((node) => {
                  const isFocused = selectedNodeId === node.id;
                  
                  // Status dot colors
                  const statusColor = 
                    node.status === 'LOAD_BURST' ? 'bg-[#e840f3]' : 
                    node.status === 'STANDBY' ? 'bg-slate-600' :
                    node.status === 'THROTTLED' ? 'bg-amber-400 animate-ping' : 'bg-emerald-400';

                  return (
                    <button
                      key={node.id}
                      onClick={() => setSelectedNodeId(node.id)}
                      className={`w-full text-left p-2.5 rounded-lg font-mono tracking-wide transition-all border flex flex-col gap-1.5 relative cursor-pointer ${
                        isFocused 
                          ? 'bg-gradient-to-r from-emerald-950/30 to-slate-950 border-emerald-500/50 text-emerald-300 shadow-[inset_0_0_10px_rgba(16,185,129,0.1),0_4px_12px_rgba(0,0,0,0.5)] scale-[1.01]' 
                          : 'bg-black/25 border-white/5 text-slate-400 hover:border-slate-850 hover:bg-white/5 hover:text-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between w-full min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-[8px] font-bold px-1 py-0.5 rounded shrink-0 ${isFocused ? 'bg-emerald-950 text-emerald-400 border border-emerald-500/20' : 'bg-slate-950 text-slate-600 border border-white/5'}`}>
                            #{node.id.toString().padStart(3, '0')}
                          </span>
                          <span className="truncate text-[9.5px] font-bold leading-none uppercase">{node.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className={`w-1.5 h-1.5 rounded-full ${statusColor}`} />
                          <span className="text-[7.5px] text-slate-500 uppercase">{node.status}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between w-full pl-6 border-t border-white/5 pt-1.5 text-[7.5px] text-slate-500 font-normal">
                        <span className="truncate max-w-[130px]">Sector: {node.categoryName}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span>Load: <strong className={node.activity > 75 ? 'text-rose-400 font-bold' : 'text-cyan-400'}>{node.activity}%</strong></span>
                          <span>•</span>
                          <span>Vol: <strong className="text-amber-500">£{node.txVolume}</strong></span>
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="text-center py-24 font-mono text-[9px] text-slate-600 uppercase">
                  No matching sub-node endpoints in current filter state
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COMMAND COCKPIT WITH SLIDERS, METRICS, AND COMPILE TERMINAL (takes 7 out of 12 columns) */}
          <div className="lg:col-span-7 flex flex-col gap-4 min-h-[670px]">
            
            {/* active node focus header card */}
            <div className="bg-[#03060f]/90 border border-emerald-500/20 p-4 rounded-xl flex flex-col relative overflow-hidden shrink-0">
              <span className="text-emerald-400 font-mono text-[9px] uppercase tracking-widest font-black block">
                Active Focal Subnode: Node #{selectedNode.id.toString().padStart(3, '0')} // Sector ID SEC-{selectedNode.categoryId.toString().padStart(2, '0')}
              </span>
              <h3 className="text-md font-black font-sans text-[#f1f5f9] mt-1.5 items-center inline-flex gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                {selectedNode.name}
              </h3>
              <div className="text-[8.5px] font-mono text-slate-400 mt-1 uppercase">
                Parent sector association: <strong className="text-slate-300 font-bold">{selectedNode.categoryName}</strong>
              </div>
              <div className="h-px bg-white/5 my-3" />

              {/* Simulation adjustable sliders */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between font-mono text-[9px] uppercase text-slate-400">
                    <span>Processing Load Override</span>
                    <span className="text-cyan-300 font-bold">{currentLoadValue}%</span>
                  </div>
                  <input 
                    type="range" 
                    min="10" 
                    max="100" 
                    value={currentLoadValue}
                    onChange={(e) => setCustomLoad(Number(e.target.value))}
                    className="w-full h-1 bg-black rounded-lg appearance-none cursor-pointer accent-cyan-400"
                  />
                  <span className="text-[7.5px] font-mono text-slate-500">Live core load slider</span>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between font-mono text-[9px] uppercase text-slate-400">
                    <span>Simulated Core Flow</span>
                    <span className="text-amber-500 font-bold">£{currentTxValue.toLocaleString()}</span>
                  </div>
                  <input 
                    type="range" 
                    min="100" 
                    max="80000" 
                    step="100"
                    value={currentTxValue}
                    onChange={(e) => setCustomTxVolume(Number(e.target.value))}
                    className="w-full h-1 bg-black rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <span className="text-[7.5px] font-mono text-slate-500">Adjust active volume flow</span>
                </div>
              </div>

              {/* Compliance options checklist */}
              <div className="flex flex-wrap gap-4 mt-3 pt-2.5 border-t border-white/5 font-mono text-[8.5px] uppercase text-slate-500">
                <div className="flex items-center gap-1.5 text-emerald-400 font-bold">
                  <ShieldCheck size={11} />
                  <span>NexoraX billing records Anchored (Secure Credential Stored Server-Side)</span>
                </div>
                <div className="flex items-center gap-1.5 text-cyan-400 font-bold">
                  <Globe size={11} />
                  <span>MNDA Compliance verified standard</span>
                </div>
                <div className="flex items-center gap-1.5 text-indigo-400 font-bold">
                  <Database size={11} />
                  <span>CloudSync-02 GDrive Mirroring: Active</span>
                </div>
              </div>
            </div>

            {/* NexoraX accounting split ledger layout */}
            <div className="bg-gradient-to-b from-[#02050f] to-[#040817] border border-white/5 p-4 rounded-xl flex flex-col shrink-0">
              <span className="font-mono text-[9px] text-[#10b981] font-black uppercase tracking-widest flex items-center gap-1.5 mb-2">
                <Coins className="w-3.5 h-3.5 text-teal-400" />
                NexoraX Real-Time Tiered Split Ledger (Focal Node #{selectedNode.id})
              </span>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-black/50 border border-white/5 p-2 rounded-xl text-left">
                  <span className="text-[8.5px] font-mono text-slate-500 uppercase block">Total Gross FLux</span>
                  <span className="text-xs font-black font-serif text-slate-100">£{currentTxValue.toLocaleString()}</span>
                </div>
                <div className="bg-black/50 border border-white/5 p-2 rounded-xl text-left border-l-2 border-l-cyan-400">
                  <span className="text-[8.5px] font-mono text-slate-500 uppercase block">Levy split (1%)</span>
                  <span className="text-xs font-black font-mono text-cyan-400">£{selectedNodeTax.levy.toLocaleString()}</span>
                </div>
                <div className="bg-black/50 border border-white/5 p-2 rounded-xl text-left border-l-2 border-l-amber-500">
                  <span className="text-[8.5px] font-mono text-slate-500 uppercase block">Gavel charge (20%)</span>
                  <span className="text-xs font-black font-mono text-amber-500">£{selectedNodeTax.gavelFee.toLocaleString()}</span>
                </div>
                <div className="bg-[#031513]/70 border border-emerald-500/20 p-2 rounded-xl text-left border-l-2 border-l-emerald-500">
                  <span className="text-[8.5px] font-mono text-emerald-500/70 uppercase block">Net SecurePayment Route</span>
                  <span className="text-xs font-black font-mono text-emerald-400">£{selectedNodeTax.netSettlement.toLocaleString()}</span>
                </div>
              </div>

              {/* Subsplit charities breakdown */}
              <div className="grid grid-cols-3 gap-2 mt-2 border-t border-white/5 pt-2 font-mono text-[8.5px] uppercase text-slate-500">
                <div className="bg-black/20 p-1 rounded text-center border border-white/5">
                  <span className="text-slate-500 text-[7px] block mb-0.5">Dunfermline Local (33.33%):</span>
                  <span className="text-slate-300 font-extrabold text-[8px]">£{selectedNodeTax.charity.toFixed(2)}</span>
                </div>
                <div className="bg-black/20 p-1 rounded text-center border border-white/5">
                  <span className="text-slate-500 text-[7px] block mb-0.5">Global Foundation (33.33%):</span>
                  <span className="text-slate-300 font-extrabold text-[8px]">£{selectedNodeTax.charity.toFixed(2)}</span>
                </div>
                <div className="bg-black/20 p-1 rounded text-center border border-white/5">
                  <span className="text-slate-500 text-[7px] block mb-0.5">United Kingdom (33.33%):</span>
                  <span className="text-slate-300 font-extrabold text-[8px]">£{selectedNodeTax.charity.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Diagnostic modules action console terminal box */}
            <div className="flex-1 glass-panel border border-white/5 rounded-xl bg-black/95 p-4 font-mono text-[9.5px] flex flex-col relative overflow-hidden min-h-[220px]">
              <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  <Terminal className="text-emerald-400 w-3.5 h-3.5 select-none" />
                  <span className="text-slate-300 uppercase tracking-wider font-mono font-bold">NODE COMPILER DIAGNOSTIC PORT</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${isRunning ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500'}`} />
                  <span className="text-[7.5px] text-slate-500 uppercase tracking-widest">{isRunning ? 'EXECUTING' : 'IDLE'}</span>
                </div>
              </div>

              {/* Logs print section */}
              <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1.5 pr-1.5 select-text text-left max-h-[170px]">
                {terminalLogs.length > 0 ? (
                  terminalLogs.map((log, idx) => {
                    let isPre = log.includes("Allah");
                    let isAudit = log.includes("---");
                    let isOk = log.includes("completed") || log.includes("verified") || log.includes("snapshotted") || log.includes("routed") || log.includes("[COMPLETED]");

                    return (
                      <div 
                        key={idx} 
                        className={
                          isPre ? 'text-amber-400 font-semibold italic border-b border-amber-500/10 pb-1 mt-1 mb-1.5' :
                          isAudit ? 'text-cyan-400 border-t border-white/5 pt-1 mt-1 font-bold tracking-widest' :
                          isOk ? 'text-emerald-400 font-extrabold' : 'text-slate-400'
                        }
                      >
                        {log}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-slate-700 italic text-center py-10">
                    [ CONSOLE STANDBY - PRESS TRIGGER NEXORAX ROUTINE BELOW ]
                  </div>
                )}
                <div ref={logsEndRef} />
              </div>

              {/* Execution panel button block */}
              <div className="border-t border-white/10 pt-3 mt-2 shrink-0 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {isRunning && (
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-3.5 h-3.5 text-emerald-400 animate-spin" />
                      <span className="text-[8.5px] text-slate-500 font-bold uppercase">Processing: {progress}%</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleExecuteSequence}
                  disabled={isRunning}
                  className={`px-5 py-2.5 rounded-lg border font-mono font-bold text-[9.5px] uppercase tracking-wider transition-all flex items-center gap-2 cursor-pointer ${
                    isRunning 
                      ? 'border-slate-800 text-slate-650 bg-black/40 cursor-not-allowed' 
                      : 'border-emerald-500/40 text-emerald-400 hover:border-emerald-300 bg-emerald-950/20 hover:bg-emerald-900/10 hover:shadow-[0_0_12px_rgba(16,185,129,0.2)]'
                  }`}
                >
                  <Play className="w-3 h-3" />
                  Trigger NexoraX Routine
                </button>
              </div>
            </div>

          </div>

        </div>

        {/* Footnote sector audits sequence */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-white/5 pt-4">
          <div className="flex flex-col gap-1 text-[8.5px] font-mono text-slate-400">
            <span className="text-white font-black uppercase text-[9px]">NexoraX Backup &amp; Live Mirroring Node Log:</span>
            <span className="text-slate-500">The backup synchronization operates automatically in the background across host directories:</span>
            <div className="bg-black/50 p-2 rounded border border-white/5 font-mono text-[8px] text-indigo-300 mt-1 uppercase select-all leading-tight">
              /var/www/nexorax_source  ↔  gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain (CloudSync-02 Mirror Protocol)
            </div>
          </div>

          <div className="flex flex-col gap-1 text-[8.5px] font-mono text-slate-400 leading-relaxed">
            <span className="text-white font-black uppercase text-[9px] block mb-1">Empirical Rules Audit Deck (Sequential Scannability):</span>
            <ol className="list-decimal list-inside space-y-0.5 text-slate-400 font-semibold uppercase text-[8px]">
              <li><strong>ACTIVE 18-SECTOR SYNAPSE GRID</strong> mapped dynamically to safeguard the NexoraX digital zone.</li>
              <li><strong>0.8s ULTRALOW LATENCY THRESHOLD</strong> actively benchmarked across all 111 sub-nodes.</li>
              <li><strong>TIERED 3% Community Allocation (1%)</strong> extracted prior to the 10% Maintenance & Hosting Fee 10% Maintenance & Hosting Fee.</li>
              <li><strong>SecurePayment finance DEPOSIT ROUTE</strong> actively locked to guarantee premium settlement NexoraXty.</li>
            </ol>
          </div>
        </div>

      </div>
    </div>
  );
}
